// src/pages/Accounting/Reconcile/components/VelaIntroBlock.tsx

import type React from "react";
import { PALETTE } from "../constants";

export function VelaIntroBlock() {
  return (
    <div>
      <p style={ledeStyle}>
        <span style={vDotStyle} aria-hidden="true">
          V
        </span>
        <span>
          <strong style={{ color: PALETTE.ink, fontWeight: 600 }}>Vela</strong>{" "}
          pre-matches most transactions by amount and date. You just confirm the
          ones she wasn't sure about and tick off anything the bank statement
          shows that Novala missed.
        </span>
      </p>

      <div style={benefitsRowStyle}>
        <Benefit
          title="Books stay honest"
          body="Every bank line gets a matching entry. No ghosts, no double-counting."
          icon={<CheckIcon />}
          position="first"
        />
        <Benefit
          title="Catch gaps early"
          body="Duplicate charges, missed fees, strange activity surface here first."
          icon={<SearchIcon />}
          position="middle"
        />
        <Benefit
          title="Tax-time ready"
          body="A reconciled year is one your accountant can file without the scramble."
          icon={<FileIcon />}
          position="last"
        />
      </div>
    </div>
  );
}

interface BenefitProps {
  title: string;
  body: string;
  icon: React.ReactNode;
  position: "first" | "middle" | "last";
}
function Benefit({ title, body, icon, position }: BenefitProps) {
  const padding =
    position === "first"
      ? "0 24px 0 0"
      : position === "last"
      ? "0 0 0 24px"
      : "0 24px";
  const borderRight =
    position === "last" ? "0" : `1px solid ${PALETTE.line}`;

  return (
    <div style={{ padding, borderRight }}>
      <div style={{ color: PALETTE.ink2, marginBottom: 10 }}>{icon}</div>
      <h4 style={benefitTitleStyle}>{title}</h4>
      <p style={benefitBodyStyle}>{body}</p>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function FileIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M9 15h6" />
      <path d="M9 11h6" />
    </svg>
  );
}

const ledeStyle: React.CSSProperties = {
  fontSize: 14,
  color: PALETTE.ink,
  margin: "0 0 24px",
  display: "flex",
  alignItems: "flex-start",
  gap: 8,
  flexWrap: "wrap",
  fontWeight: 500,
  maxWidth: 820,
};

const vDotStyle: React.CSSProperties = {
  width: 18,
  height: 18,
  borderRadius: "50%",
  background: PALETTE.vela,
  color: "#fff",
  fontWeight: 700,
  fontSize: 10,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  marginTop: 2,
  flexShrink: 0,
};

const benefitsRowStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr",
  gap: 0,
  marginBottom: 36,
  paddingBottom: 24,
  borderBottom: `1px solid ${PALETTE.line}`,
  maxWidth: 980,
};

const benefitTitleStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: PALETTE.ink,
  margin: "0 0 4px",
};

const benefitBodyStyle: React.CSSProperties = {
  fontSize: 13,
  color: PALETTE.ink2,
  margin: 0,
  fontWeight: 500,
  lineHeight: 1.5,
};